import React from 'react';
import { ArrowRight, FileText, ClipboardList } from 'lucide-react';
import { MySurvey } from '../../types';
import { surveyProgressPct, surveyNoteLine, surveyStatusMeta, surveyCta } from '../../../../utils/survey';
import { surveyTileToken } from '../../../../utils/surveyInsights';
import { SURVEY_STATUS } from '../../../../utils/constants';

interface SurveyCardProps {
  survey: MySurvey;
  onViewInfo: (surveyId: string) => void;
  onViewResponse?: (responseId: string) => void;
}

/** Figma survey card (dc.html:2077): tile + title/sub + pill, note, bar, CTA link. */
const SurveyCard: React.FC<SurveyCardProps> = ({ survey, onViewInfo, onViewResponse }) => {
  const statusMeta = surveyStatusMeta(survey.status);
  const cta = surveyCta(survey.status);
  const tile = surveyTileToken(survey._id);
  const progress = surveyProgressPct(survey.answeredCount, survey.questionCount);

  const openSurvey = () => {
    if (survey.status === SURVEY_STATUS.COMPLETED && onViewResponse && survey.responseId) {
      onViewResponse(survey.responseId);
    } else {
      onViewInfo(survey._id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openSurvey();
    }
  };

  const CtaIcon = cta.icon === 'file' ? FileText : ArrowRight;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openSurvey}
      onKeyDown={handleKeyDown}
      aria-label={`${survey.title} — ${cta.label}`}
      className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] p-[22px] cursor-pointer transition-all duration-200 hover:-translate-y-[3px] hover:shadow-[var(--shadow-md)] flex flex-col h-full focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none"
    >
      <div className="flex items-center gap-3">
        <span className={`w-[46px] h-[46px] rounded-[13px] flex-none grid place-items-center ${tile.tileBg}`}>
          <ClipboardList className={`w-[22px] h-[22px] ${tile.tileColor}`} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-heading-sm text-[var(--color-text-primary)] truncate">
            {survey.title}
          </div>
          <div className="text-caption text-[var(--color-text-tertiary)] mt-0.5 truncate">
            {survey.questionCount} {survey.questionCount === 1 ? 'question' : 'questions'}
          </div>
        </div>
        <span
          className={`inline-flex items-center px-[11px] py-1 rounded-full text-caption font-bold whitespace-nowrap flex-none ${statusMeta.pillBg} ${statusMeta.pillColor}`}
        >
          {statusMeta.label}
        </span>
      </div>

      <div className="mt-[18px] flex items-center justify-between mb-2">
        <span className="text-caption font-semibold text-[var(--color-text-secondary)] truncate">
          {surveyNoteLine(survey.status, survey.answeredCount, survey.questionCount)}
        </span>
        <span className="text-body-sm font-bold text-[var(--color-text-primary)] flex-none">
          {progress}%
        </span>
      </div>
      <div className="h-2 rounded-full bg-[var(--color-bg-subtle)] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${progress}%`, background: 'var(--color-primary-700)' }}
        />
      </div>

      <div className="mt-4 flex items-center gap-1.5 text-body-sm font-bold text-[var(--color-primary-700)]">
        {cta.label}
        <CtaIcon className="w-[15px] h-[15px]" aria-hidden="true" />
      </div>
    </div>
  );
};

export default SurveyCard;
